import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, ChevronDown } from 'lucide-react';
import { NAV_LINKS, SERVICE_LINKS, SOLUTION_LINKS } from './constants';
import { useUser } from '@/shared/hooks/useUser';
import { logout } from '@/shared/api/authentication-api';
import { useToast } from '@/hooks/use-toast';

interface NavigationProps {
  onNavigate: (sectionId: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { user, loading } = useUser();

  const handleMobileNavClick = (sectionId: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      onNavigate(sectionId);
    }, 600);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      localStorage.removeItem('token');
      toast({
        title: 'Success',
        description: 'Logged out successfully',
        variant: 'default',
        className: 'bg-maintenance-blue text-white',
        duration: 3000,
      });
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: 'Error',
        description: 'Failed to logout. Please try again.',
        variant: 'destructive',
        duration: 3000,
      });
      setIsLoggingOut(false);
    }
  };

  return (
    <nav className={`flex justify-between items-center bg-maintenance-navy/80 backdrop-blur-sm -m-2 md:-m-5 px-2 py-2 md:p-5 shadow-lg`}>
      {/* Mobile Menu */}
      <div className="md:hidden flex items-center">
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white h-8 w-8 p-0 hover:bg-white/10">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className={`bg-maintenance-navy text-white border-r border-white/10`}>
            <SheetHeader>
              <SheetTitle className="text-white">Menu</SheetTitle>
            </SheetHeader>
            <div className="flex flex-col space-y-4 mt-6">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => handleMobileNavClick(link.id)}
                  className={`text-left text-white/70 hover:text-white transition-colors`}
                >
                  {link.label}
                </button>
              ))}

              <div className="space-y-2">
                <div className={`text-left text-maintenance-blue font-medium text-sm`}>Services</div>
                <div className="pl-4 space-y-2">
                  {SERVICE_LINKS.map((link) => (
                    <button
                      key={link.id}
                      className={`block text-left text-white/70 hover:text-white transition-colors`}
                      onClick={() => {
                        if ('href' in link) {
                          window.open(link.href, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className={`text-left text-maintenance-blue font-medium text-sm`}>Solutions</div>
                <div className="pl-4 space-y-2">
                  {SOLUTION_LINKS.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => handleMobileNavClick(link.id)}
                      className={`block text-left text-white/70 hover:text-white transition-colors`}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Logo */}
      <div className="flex-1 md:flex-none flex items-center justify-center md:justify-start">
        <div className="rounded-md px-2 py-1 md:px-3 md:py-2 inline-flex items-center">
          <img
            src="/static/images/iv360-p-500-white.png"
            alt="Ironview 360"
            className="h-8 sm:h-10 md:h-10 lg:h-12 w-auto object-contain"
          />
        </div>
      </div>

      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center space-x-6">
        <button
          onClick={() => onNavigate('about-us')}
          className={`text-white hover:text-white/70 transition-colors font-medium text-lg`}
        >
          About Us
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={`text-white hover:text-white/70 transition-colors font-medium text-lg p-0 h-auto hover:bg-transparent`}
            >
              Services <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={`bg-maintenance-navy border border-white/10`}>
            {SERVICE_LINKS.map((link) => (
              <DropdownMenuItem
                key={link.id}
                className="text-white hover:bg-maintenance-blue/20 cursor-pointer transition-colors"
                onSelect={() => {
                  if ('href' in link) {
                    window.open(link.href, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                {link.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          onClick={() => onNavigate('demos')}
          className={`text-white hover:text-white/70 transition-colors font-medium text-lg`}
        >
          Demos
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={`text-white hover:text-white/70 transition-colors font-medium text-lg p-0 h-auto hover:bg-transparent`}
            >
              Solutions <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={`bg-maintenance-navy border border-white/10`}>
            {SOLUTION_LINKS.map((link) => (
              <DropdownMenuItem
                key={link.id}
                onSelect={(e) => {
                  e.preventDefault();
                  onNavigate(link.id);
                }}
                className="text-white hover:bg-maintenance-blue/20 cursor-pointer transition-colors"
              >
                {link.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Auth Buttons */}
      <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
        {loading ? (
          <div className="h-10 w-24 animate-pulse bg-white/10 rounded-md" />
        ) : user ? (
          <>
            <button
              className={`bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors px-4 py-2 rounded-md text-xs sm:text-sm md:text-base whitespace-nowrap`}
              onClick={() => navigate('/app')}
            >
              Dashboard
            </button>
            <button
              className={`bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors px-4 py-2 rounded-md text-xs sm:text-sm md:text-base whitespace-nowrap`}
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </button>
          </>
        ) : (
          <>
            <button
              className={`bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors px-4 py-2 rounded-md text-xs sm:text-sm md:text-base whitespace-nowrap`}
              onClick={() => navigate('/login')}
            >
              Login
            </button>
            <button
              className={`bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors px-4 py-2 rounded-md text-xs sm:text-sm md:text-base whitespace-nowrap`}
              onClick={() => navigate('/signup')}
            >
              Sign Up
            </button>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
